/**
 * Alpine.js Mobile Photo Viewer Component
 *
 * Fullscreen immersive photo viewer for mobile with:
 * - Touch/swipe navigation between photos
 * - Overlay UI toggle on tap
 * - Like button with optimistic API updates
 * - Comments bottom sheet
 * - Action menu (delete, moderate, report, reverse image search)
 *
 * Composes photoViewerCore for shared state/logic and adds mobile-specific behavior.
 */

import { photoViewerCore } from './photo-viewer-core.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

export function mobilePhotoViewer(config) {
    const core = photoViewerCore(config);

    return {
        ...core,

        // Mobile-only: UI overlay visibility
        showUI: true,

        // Swipe tracking
        touchStartX: 0,
        touchStartY: 0,
        touchCurrentX: 0,
        directionLocked: false,
        isHorizontalSwipe: false,
        isSwiping: false,
        swipeThreshold: 0.15,
        swipeJustOccurred: false,

        init() {
            lockBodyScroll();
            this._preloadAdjacentImages(config);
            this._popstateHandler = () => window.location.reload();
            window.addEventListener('popstate', this._popstateHandler);

            // Move viewer to body so it escapes the mobile scroll container
            // which clips fixed children on iOS Safari
            this._originalParent = this.$el.parentNode;
            this._originalNext = this.$el.nextSibling;
            document.body.appendChild(this.$el);

            this._bottomNav = document.querySelector('[data-component="mobile-bottom-nav"]');
            if (this._bottomNav) this._bottomNav.style.display = 'none';
        },

        destroy() {
            unlockBodyScroll();
            this._commentObserver?.disconnect();
            window.removeEventListener('popstate', this._popstateHandler);

            if (this._originalParent) {
                this._originalParent.insertBefore(this.$el, this._originalNext);
            }
            if (this._bottomNav) this._bottomNav.style.display = '';
        },

        _getCommentsContainer() {
            return this.$refs.commentsSheet || null;
        },

        _refetchCommentsOnNav() {
            this.commentsOpen = false;
        },

        // --- UI Toggle ---

        toggleUI() {
            if (this.swipeJustOccurred) {
                this.swipeJustOccurred = false;
                return;
            }
            this.showUI = !this.showUI;
        },

        // --- Touch / Swipe ---

        onTouchStart(e) {
            if (this.isNavigating || this.commentsOpen || this.menuOpen) return;
            const touch = e.touches[0];
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            this.touchCurrentX = touch.clientX;
            this.directionLocked = false;
            this.isHorizontalSwipe = false;
            this.isSwiping = false;
        },

        onTouchMove(e) {
            if (this.isNavigating || this.commentsOpen || this.menuOpen) return;
            const touch = e.touches[0];
            const deltaX = touch.clientX - this.touchStartX;
            const deltaY = touch.clientY - this.touchStartY;

            if (!this.directionLocked && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
                this.directionLocked = true;
                this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
            }

            if (this.directionLocked && this.isHorizontalSwipe) {
                e.preventDefault();
                this.isSwiping = true;
                this.touchCurrentX = touch.clientX;
            }
        },

        onTouchEnd() {
            if (!this.isSwiping) return;

            const deltaX = this.touchCurrentX - this.touchStartX;
            const screenWidth = window.innerWidth;
            const swipeRatio = Math.abs(deltaX) / screenWidth;

            if (swipeRatio > this.swipeThreshold) {
                if (deltaX < 0 && this.nextUrl) {
                    this.navigateTo(1);
                } else if (deltaX > 0 && this.prevUrl) {
                    this.navigateTo(-1);
                }
            }

            this.isSwiping = false;
            this.directionLocked = false;
            this.isHorizontalSwipe = false;
            this.touchCurrentX = this.touchStartX;
            this.swipeJustOccurred = true;
            setTimeout(() => { this.swipeJustOccurred = false; }, 250);
        },

        get imageTransformStyle() {
            if (this.isNavigating) {
                const offset = this.navigateDirection * window.innerWidth;
                return `transform: translateX(${offset}px); transition: transform 0.15s ease-out;`;
            }
            if (!this.isSwiping) return '';

            let deltaX = this.touchCurrentX - this.touchStartX;

            if ((deltaX > 0 && !this.prevUrl) || (deltaX < 0 && !this.nextUrl)) {
                deltaX *= 0.3;
            }

            return `transform: translateX(${deltaX}px);`;
        }
    };
}

export default mobilePhotoViewer;
